import { Button as AntButton, Modal, Select, Table, TableColumnsType } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IMember } from '../../../../models/messages.model';
import { RootState } from '../../../../store/rootReducer';
import Checkbox from '../../../../components/Checkbox';
import config from '../../../../lib/config';
import Pagination from '../../../../components/Pagination';
import Button from '../../../../components/Button';
import _ from 'lodash';
import SelectCampus from '../../../SelectCampus';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  members: IMember[] | undefined;
  memberCodes: string[];
  setMemberCodes: (memberCodes: string[]) => void;
}

const SelectMemberModal: FC<IProps> = ({
  visible,
  setVisible,
  members,
  memberCodes,
  setMemberCodes
}) => {
  const { t, i18n } = useTranslation();
  const { campusData } = useSelector((state: RootState) => state.campusData);  
  const { groups } = useSelector((state: RootState) => state.group);

  const [data, setData] = useState<IMember[]>([]);
  const [page, setPage] = useState(1);
  const [campusCode, setCampusCode] = useState<string>();
  const [groupNo, setGroupNo] = useState<number>();
  const [name, setName] = useState<string>('');
  const [selects, setSelects] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);

  useEffect(() => {
    if (members) setData(members);
  }, [members]);

  const onSetSelects = (memberCode: string) => {
    if (selects.includes(memberCode)) {
      setSelects(selects.filter((item: any) => item !== memberCode));
      setIsSelectAll(false);
    } else {
      setSelects([...selects, memberCode]);
    }
  }

  const onSelectAll = () => {
    if (isSelectAll) {
      setSelects([]);
      setIsSelectAll(false);
    } else {
      const _selects = _.uniq(data.map((item: any) => item.memberCode));
      setSelects(_selects);
      setIsSelectAll(true);
    }
  }

  const onSearch = () => {
    if (!members) return;

    let _data = _.cloneDeep(members);
    console.log('data', _data)

    if (campusCode) {
      _data = _data.filter(item => item.campusCode === campusCode);
    }
    if (groupNo) {
      _data = _data.filter(item => item.groupNo === String(groupNo));
    }
    if (name) {
      _data = _data.filter(item => item.memKorName.includes(name));
    }

    setData(_data);
    setPage(1);
  }

  const onClose = () => {
    setIsSelectAll(false);
    setSelects([]);
    setVisible(false);
  }

  const columns: TableColumnsType<IMember> = [
    {
      title: (
        <Checkbox
          checked={isSelectAll}
          onClickCapture={(e: any) => {
            e.stopPropagation();
            onSelectAll();
          }}
          text=""
        />
      ),
      dataIndex: 'memberCode',
      align: 'center',
      render: (value: string) => {
        let checked = false;
        if (selects.includes(value) || isSelectAll) checked = true;

        return (
          <Checkbox
            checked={checked}
            onChange={() => onSetSelects(value)}
            onClickCapture={e => {
              e.stopPropagation();
              onSetSelects(value);
            }}
            text=""
          />
        );
      }
    },
    {
      title: t('message.smsWrite.column3'),
      dataIndex: 'campusName',
      align: 'center',
      render: (value, record) => i18n.language === 'ko' ? value : record.campusEnglishName,
    },
    {
      title: t('common.column.group'),
      dataIndex: 'groKorName',
      align: 'center',
      render: (value, record) => i18n.language === 'ko' ? value : record.groEngName,
    },
    {
      title: t('message.write.1'),
      dataIndex: 'memKorName',
      align: 'center',
      render: (value, record) => i18n.language === 'ko' ? value : record.memEngName,
    },
    {
      title: t('auth.login.label1'),
      dataIndex: 'webId',
      align: 'center',
    },
    {
      title: t('message.smsWrite.column5'),
      dataIndex: 'mobilePhone',
      align: 'center',
    },
  ];

  return (
    <>
      <Modal
        title={t('message.smsWrite.button1')}
        visible={visible}
        onCancel={onClose}
        width={600}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              type="primary"
              className='btn-basic pink'
              onClick={() => {
                setMemberCodes(_.uniq([...memberCodes, ...selects]));
                onClose();
              }}
            >
              {t('common.button.select')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={() => {
                setIsSelectAll(false);
                setSelects([]);
                setVisible(false);
              }}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="flex flex-justify-end comm-gap">
          <div className="w-150p">
            <SelectCampus
              className="w-100"
              placeholder={t('common.menu.campus')}
              value={campusCode}
              onChange={(value: string) => setCampusCode(value)}
            />
          </div>
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('common.column.group')}
              value={groupNo}
              onChange={value => setGroupNo(value)}
            >
              <Select.Option value="">{t('common.text.all')}</Select.Option>
              {
                groups?.map((item: any) => {
                  return (
                    <Select.Option
                      key={item.groupNo}
                      value={item.groupNo}
                    >
                      {item.korName}
                    </Select.Option>
                  )
                })
              }
            </Select>
          </div>
          <input
            type="text"
            className="form-control w-150p"
            placeholder={t('message.smsWrite.column4')}
            onChange={(e => setName(e.target.value))}
            onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
          />
          <div className='comm-btn__area'>
            <Button
              className="btn-basic small black"
              onClick={onSearch}
              text={t('common.button.search')}
            />   
          </div>       
        </div>
        <div className='comm-table__area mt-3'>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.miniPageSize
            }}
          />          
        </div>
        <div className="comm-bx__between">
          <div className="common-paging center">
            <Pagination
              count={data?.length || 0}
              page={page}
              setPage={setPage}
              mini
            />            
          </div>
        </div>
      </Modal>
    </>
  );
}

export default SelectMemberModal;
